import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Chip,
  IconButton,
  TableSortLabel,
  Tooltip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  MedicalServices as MedicalIcon,
  Clear as ClearIcon,
  Pets as PetsIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import DateField from './DateField';
import { formatDate } from '../utils/dateUtils';
import MunicipalityExpenseDialog from './MunicipalityExpenseDialog';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface MedicalExpense {
  id: number;
  source_table: string;
  entity_id: number;
  cat_name?: string;
  chip_id?: string;
  municipality?: string;
  event_type?: string;
  event_date?: string;
  cost?: number;
  veterinary_name?: string;
  invoice_file_path?: string;
  notes?: string;
  fiv_result?: string;
  felv_result?: string;
  external?: boolean;
  recovery_waived?: boolean;
}


const encodeFilePath = (path: string): string => {
  const lastSlash = path.lastIndexOf('/');
  if (lastSlash === -1) return encodeURIComponent(path);
  const dir = path.substring(0, lastSlash + 1);
  const filename = path.substring(lastSlash + 1);
  return dir + encodeURIComponent(filename);
};

const DetailField: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <Box sx={{ mb: 0.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
      {value || '-'}
    </Typography>
  </Box>
);

const ExpensesList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [expenses, setExpenses] = useState<MedicalExpense[]>([]);
  const [filteredExpenses, setFilteredExpenses] = useState<MedicalExpense[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterMunicipality, setFilterMunicipality] = useState('');

  const [selectedExpense, setSelectedExpense] = useState<MedicalExpense | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<any>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Municipality-level expenses (cumulative external invoices)
  const [munExpenses, setMunExpenses] = useState<any[]>([]);
  const [meDialogOpen, setMeDialogOpen] = useState(false);
  const [meEditing, setMeEditing] = useState<any | null>(null);
  const [meDeleteTarget, setMeDeleteTarget] = useState<any | null>(null);

  // Resizable columns
  const [colWidths, setColWidths] = useState<Record<string, number>>({
    event_date: 100, event_type: 130, cat_name: 140, chip_id: 130,
    municipality: 120, veterinary_name: 130, notes: 160, cost: 80, invoice: 60,
  });
  const resizingRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null);
  const handleResizeMouseDown = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    resizingRef.current = { col, startX: e.clientX, startWidth: colWidths[col] };
    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const newWidth = Math.max(40, resizingRef.current.startWidth + ev.clientX - resizingRef.current.startX);
      setColWidths(prev => ({ ...prev, [resizingRef.current!.col]: newWidth }));
    };
    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [colWidths]);

  // Sortable columns
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortedExpenses = useMemo(() => {
    if (!sortCol) return filteredExpenses;
    return [...filteredExpenses].sort((a, b) => {
      const ar = (a as any)[sortCol] ?? '';
      const br = (b as any)[sortCol] ?? '';
      const an = Number(ar), bn = Number(br);
      if (!isNaN(an) && !isNaN(bn) && ar !== '' && br !== '') {
        return sortDir === 'asc' ? an - bn : bn - an;
      }
      return sortDir === 'asc'
        ? String(ar).toLowerCase().localeCompare(String(br).toLowerCase())
        : String(br).toLowerCase().localeCompare(String(ar).toLowerCase());
    });
  }, [filteredExpenses, sortCol, sortDir]);

  useEffect(() => {
    loadExpenses();
    loadMunicipalities();
    loadMunExpenses();
  }, []);

  const loadMunExpenses = async () => {
    try {
      const res = await api.get('/api/tables/public/municipality_expenses/data?limit=1000');
      const cols: string[] = res.data.columns || [];
      const idx = (n: string) => cols.indexOf(n);
      setMunExpenses((res.data.rows || []).map((r: any[]) => ({
        id: r[idx('id')],
        municipality: r[idx('municipality')],
        expense_date: r[idx('expense_date')],
        description: r[idx('description')],
        amount: r[idx('amount')],
        invoice_number: r[idx('invoice_number')],
        invoice_file_path: r[idx('invoice_file_path')],
        notes: r[idx('notes')],
      })));
    } catch (error) {
      console.error('Error loading municipality expenses:', error);
    }
  };

  const filteredMunExpenses = useMemo(() => {
    let result = [...munExpenses];
    if (filterDateFrom) result = result.filter(e => e.expense_date && e.expense_date >= filterDateFrom);
    if (filterDateTo) result = result.filter(e => e.expense_date && e.expense_date <= filterDateTo);
    if (filterMunicipality) result = result.filter(e => e.municipality === filterMunicipality);
    return result.sort((a, b) => (b.expense_date || '').localeCompare(a.expense_date || ''));
  }, [munExpenses, filterDateFrom, filterDateTo, filterMunicipality]);

  const translateEventType = (et: string | null | undefined): string => {
    if (!et) return '-';
    const map: Record<string, string> = {
      'Vaccine': t('medical.eventTypes.vaccine'),
      'Sterilization': t('medical.eventTypes.sterilization'),
      'Treatment': t('medical.eventTypes.treatment'),
      'Delivery': t('medical.eventTypes.delivery'),
      'Surgery': t('medical.eventTypes.surgery'),
      'Checkup': t('medical.eventTypes.checkup'),
      'test_fiv_felv': t('medical.eventTypes.testFivFelv'),
      'Travel Expenses': t('medical.eventTypes.travelExpenses'),
      'Other': t('medical.eventTypes.other'),
    };
    return map[et] || et;
  };

  // The shared dialog loads the invoice itself, sterilizations included; it only
  // needs to know which one to edit.
  const openMeEdit = (e: any) => {
    setMeEditing(e);
    setMeDialogOpen(true);
  };

  const handleMeDelete = async () => {
    if (!meDeleteTarget) return;
    try {
      await api.delete('/api/records', { data: { table: 'municipality_expenses', schema: 'public', record_id: meDeleteTarget.id } });
      setMeDeleteTarget(null);
      setMessage({ type: 'success', text: t('expense.muniDeleted') });
      setTimeout(() => setMessage(null), 3000);
      loadMunExpenses();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.detail || 'Error' });
    }
  };

  useEffect(() => {
    applyFilters();
  }, [expenses, filterDateFrom, filterDateTo, filterMunicipality]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_URL}/api/medical-events/all`);
      setExpenses(response.data as MedicalExpense[]);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Failed to load expenses: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const loadMunicipalities = async () => {
    try {
      const response = await api.get('/api/tables/public/municipalities/data?limit=100');
      setMunicipalities(response.data.rows.map((row: any[]) => row[0]));
    } catch (error) {
      console.error('Error loading municipalities:', error);
    }
  };

  const applyFilters = () => {
    let result = [...expenses];
    if (filterDateFrom) result = result.filter(e => e.event_date && e.event_date >= filterDateFrom);
    if (filterDateTo) result = result.filter(e => e.event_date && e.event_date <= filterDateTo);
    if (filterMunicipality) result = result.filter(e => e.municipality === filterMunicipality);
    result.sort((a, b) => (b.event_date || '').localeCompare(a.event_date || ''));
    setFilteredExpenses(result);
  };

  const clearFilters = () => {
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterMunicipality('');
  };

  const handleEditClick = () => {
    if (!selectedExpense) return;
    setEditFormData({
      event_type: selectedExpense.event_type || '',
      event_date: selectedExpense.event_date || '',
      cost: selectedExpense.cost || '',
      veterinary_name: selectedExpense.veterinary_name || '',
      notes: selectedExpense.notes || '',
      fiv_result: selectedExpense.fiv_result || '',
      felv_result: selectedExpense.felv_result || '',
      recovery_waived: !!selectedExpense.recovery_waived,
    });
    setSelectedFile(null);
    setEditDialogOpen(true);
  };

  const handleUpdateExpense = async () => {
    if (!selectedExpense) return;
    try {
      let filePath = selectedExpense.invoice_file_path;
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const uploadResponse = await api.post('/api/upload/invoice', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        filePath = uploadResponse.data.file_path;
      }

      const updatedData = { ...editFormData };
      if (filePath) updatedData.invoice_file_path = filePath;
      if (!updatedData.fiv_result) delete updatedData.fiv_result;
      if (!updatedData.felv_result) delete updatedData.felv_result;
      // The recovery decision has a side effect (creating or deleting the stay),
      // so it goes through its own endpoint rather than the generic record write.
      const recoveryWaived = !!updatedData.recovery_waived;
      delete updatedData.recovery_waived;

      await api.put('/api/records', {
        table: selectedExpense.source_table,
        schema: 'public',
        record_id: selectedExpense.id,
        data: updatedData,
      });

      if (updatedData.event_type === 'Sterilization' && selectedExpense.external) {
        await api.put(`/api/medical/${selectedExpense.id}/recovery`, { waived: recoveryWaived });
      }

      setMessage({ type: 'success', text: 'Expense updated successfully!' });
      setEditDialogOpen(false);
      setSelectedFile(null);
      setSelectedExpense({
        ...selectedExpense, ...updatedData,
        recovery_waived: recoveryWaived, invoice_file_path: filePath,
      });
      loadExpenses();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Failed to update: ${error.response?.data?.detail || error.message}` });
    }
  };

  const handleDeleteExpense = async () => {
    if (!selectedExpense) return;
    try {
      await api.delete('/api/records', {
        data: { table: selectedExpense.source_table, schema: 'public', record_id: selectedExpense.id },
      });
      setMessage({ type: 'success', text: 'Expense deleted successfully!' });
      setDeleteDialogOpen(false);
      setSelectedExpense(null);
      loadExpenses();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Failed to delete: ${error.response?.data?.detail || error.message}` });
    }
  };

  const totalCost = filteredExpenses.reduce((sum, e) => sum + (Number(e.cost) || 0), 0);

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#667eea' }}>
          {t('expense.medicalExpenses')} ({filteredExpenses.length})
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            borderColor: '#667eea', color: '#667eea', borderRadius: 2,
            textTransform: 'none', fontWeight: 600,
            '&:hover': { borderColor: '#5568d3', background: 'rgba(102,126,234,0.05)' },
          }}
        >
          {t('common.backToMenu')}
        </Button>
      </Box>

      {/* Summary Card */}
      <Box sx={{ mb: 1.5, display: 'flex', gap: 2 }}>
        <Paper sx={{ p: 1.5, flex: 1, borderRadius: 2, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>{t('expense.medicalExpenses')}</Typography>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
            {'\u20AC'}{totalCost.toFixed(2)}
          </Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 2, borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>{t('expense.records')}</Typography>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
            {filteredExpenses.length}
          </Typography>
        </Paper>
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, boxShadow: '0 2px 8px rgba(102,126,234,0.12)' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('expense.filterExpenses')}
        </Typography>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={6} md={2}>
            <DateField label={t('expense.fromDate')} value={filterDateFrom} onChange={setFilterDateFrom} />
          </Grid>
          <Grid item xs={6} md={2}>
            <DateField label={t('expense.toDate')} value={filterDateTo} onChange={setFilterDateTo} />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('cat.municipality')}</InputLabel>
              <Select
                value={filterMunicipality}
                label={t('cat.municipality')}
                onChange={(e) => setFilterMunicipality(e.target.value)}
              >
                <MenuItem value="">{t('expense.allMunicipalities')}</MenuItem>
                {municipalities.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth variant="outlined" onClick={clearFilters} startIcon={<ClearIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Master-Detail Layout.
          The height adapts to the rows instead of being fixed: with a fixed one a
          short list left a screenful of empty table, pushing the generic /
          multi-cat expenses section below the fold, where it looked absent. The
          cap keeps a long list scrolling inside its own box, under the sticky
          header, rather than stretching the page. */}
      <Box sx={{ display: 'flex', gap: 1.5, maxHeight: 'calc(100vh - 320px)', minHeight: 300 }}>
        {/* Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            flex: selectedExpense ? '0 0 58%' : '1 1 100%',
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(102,126,234,0.15)',
            transition: 'flex 0.3s ease',
            overflow: 'auto',
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
            <colgroup>
              {(['event_date','event_type','cat_name','chip_id','municipality','veterinary_name','notes','cost','invoice'] as const).map(col => (
                <col key={col} style={{ width: colWidths[col] }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={{ '& th': { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 600, fontSize: '0.75rem', padding: '6px 10px', position: 'relative', overflow: 'hidden', whiteSpace: 'nowrap' } }}>
                {([
                  { col: 'event_date', label: t('common.date') },
                  { col: 'event_type', label: t('medical.eventType') },
                  { col: 'cat_name', label: t('catFields.name') },
                  { col: 'chip_id', label: t('report.chipId') },
                  { col: 'municipality', label: t('cat.municipality') },
                  { col: 'veterinary_name', label: t('medical.veterinary') },
                  { col: 'notes', label: t('cat.notes') },
                  { col: 'cost', label: t('medical.cost') },
                  { col: 'invoice', label: t('medical.invoice') },
                ] as const).map(({ col, label }) => (
                  <TableCell key={col} onClick={() => col !== 'invoice' && handleSort(col)}
                    sx={{ cursor: col !== 'invoice' ? 'pointer' : 'default', userSelect: 'none',
                          textAlign: col === 'cost' ? 'right' : col === 'invoice' ? 'center' : 'left' }}>
                    {col !== 'invoice' ? (
                      <TableSortLabel
                        active={sortCol === col}
                        direction={sortCol === col ? sortDir : 'asc'}
                        sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.7) !important' } }}
                      >
                        {label}
                      </TableSortLabel>
                    ) : label}
                    <Box
                      onMouseDown={(e) => handleResizeMouseDown(col, e)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 5,
                            cursor: 'col-resize', zIndex: 1, '&:hover': { background: 'rgba(255,255,255,0.35)' } }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary">{t('common.loading')}</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary">{t('expense.noExpensesFound')}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedExpenses.map((expense) => (
                  <TableRow
                    key={expense.id}
                    hover
                    onClick={() => setSelectedExpense(expense)}
                    sx={{
                      cursor: 'pointer',
                      '& td': { padding: '4px 8px', fontSize: '0.8rem' },
                      backgroundColor: selectedExpense?.id === expense.id ? 'rgba(102,126,234,0.12)' : 'inherit',
                      borderLeft: selectedExpense?.id === expense.id ? '3px solid #667eea' : '3px solid transparent',
                      '&:hover': {
                        backgroundColor: selectedExpense?.id === expense.id ? 'rgba(102,126,234,0.18)' : 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    <TableCell>{formatDate(expense.event_date) === 'N/A' ? '-' : formatDate(expense.event_date)}</TableCell>
                    <TableCell>
                      <Chip
                        label={translateEventType(expense.event_type)}
                        size="small"
                        sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', fontWeight: 600, fontSize: '0.65rem' }}
                      />
                    </TableCell>
                    <TableCell>{expense.cat_name || '-'}</TableCell>
                    <TableCell>{expense.chip_id || t('cat.missing')}</TableCell>
                    <TableCell>{expense.municipality || '-'}</TableCell>
                    <TableCell>{expense.veterinary_name || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.75rem' }}>{expense.notes || '-'}</TableCell>
                    <TableCell align="right">
                      {expense.cost ? `\u20AC${Number(expense.cost).toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {expense.invoice_file_path && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`${API_URL}${encodeFilePath(expense.invoice_file_path || '')}`, '_blank');
                          }}
                          sx={{ color: '#667eea' }}
                        >
                          <AttachFileIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Detail Panel */}
        {selectedExpense && (
          <Paper
            elevation={0}
            sx={{
              flex: '0 0 40%', borderRadius: 3,
              boxShadow: '0 4px 12px rgba(102,126,234,0.15)',
              overflow: 'auto', display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Header */}
            <Box sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white' }}>
                <MedicalIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  {t('medical.medicalExpense')}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  ID: {selectedExpense.id} | {t('cat.cat')}: {selectedExpense.cat_name}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedExpense(null)} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ p: 1, display: 'flex', gap: 0.5, borderBottom: '1px solid #eee' }}>
              <Button
                variant="contained" startIcon={<EditIcon />} onClick={handleEditClick} size="small"
                sx={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="contained" startIcon={<DeleteIcon />} onClick={() => setDeleteDialogOpen(true)}
                size="small" color="error"
                sx={{ flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                {t('common.delete')}
              </Button>
              <Button
                variant="outlined" startIcon={<PetsIcon />}
                onClick={() => navigate(`/cat-record/${selectedExpense.entity_id}`)}
                size="small"
                sx={{ flex: 1, textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#667eea', color: '#667eea' }}
              >
                {t('cat.viewCat')}
              </Button>
            </Box>

            {/* Details */}
            <Box sx={{ p: 1.5, overflow: 'auto', flex: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#f5576c', fontWeight: 700, mb: 0.75, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                {t('medical.eventDetails')}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <DetailField label={t('medical.eventType')} value={translateEventType(selectedExpense.event_type)} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('medical.eventDate')} value={formatDate(selectedExpense.event_date)} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('medical.cost')} value={selectedExpense.cost ? `\u20AC${Number(selectedExpense.cost).toFixed(2)}` : undefined} />
                </Grid>
                <Grid item xs={6}>
                  <DetailField label={t('medical.veterinary')} value={selectedExpense.veterinary_name} />
                </Grid>
              </Grid>

              {(selectedExpense.fiv_result || selectedExpense.felv_result) && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ color: '#f5576c', fontWeight: 700, mb: 0.75, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                    {t('medical.additionalInfo')}
                  </Typography>
                  <Grid container spacing={1}>
                    {selectedExpense.fiv_result && (
                      <Grid item xs={6}>
                        <DetailField label={t('medical.fivResult')} value={selectedExpense.fiv_result} />
                      </Grid>
                    )}
                    {selectedExpense.felv_result && (
                      <Grid item xs={6}>
                        <DetailField label={t('medical.felvResult')} value={selectedExpense.felv_result} />
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {selectedExpense.notes && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <DetailField label={t('common.notes')} value={selectedExpense.notes} />
                </>
              )}

              {selectedExpense.invoice_file_path && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Button
                    variant="outlined" startIcon={<AttachFileIcon />} size="small" fullWidth
                    onClick={() => window.open(`${API_URL}${encodeFilePath(selectedExpense.invoice_file_path || '')}`, '_blank')}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#667eea', color: '#667eea' }}
                  >
                    {t('medical.viewInvoice')}
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* ---- Municipality expenses (cumulative external invoices) ---- */}
      <Box sx={{ mt: 4, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalanceIcon sx={{ color: '#667eea' }} />
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('expense.municipalExpenses')}</Typography>
            {munExpenses.length > 0 && (
              <Chip label={munExpenses.length} size="small" sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 700 }} />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">{t('expense.municipalExpensesHint')}</Typography>
        </Box>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, background: '#f5f6ff' } }}>
              <TableCell>{t('expense.date')}</TableCell>
              <TableCell>{t('cat.municipality')}</TableCell>
              <TableCell>{t('expense.invoiceNumber')}</TableCell>
              <TableCell>{t('expense.description')}</TableCell>
              <TableCell align="right">{t('expense.amount')}</TableCell>
              <TableCell align="center">{t('expense.invoice')}</TableCell>
              <TableCell align="center">{t('external.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMunExpenses.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{t('expense.noMunicipal')}</Typography></TableCell></TableRow>
            ) : (
              filteredMunExpenses.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{formatDate(e.expense_date)}</TableCell>
                  <TableCell>{e.municipality}</TableCell>
                  <TableCell>{e.invoice_number || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description || '-'}</TableCell>
                  <TableCell align="right">€{Number(e.amount || 0).toFixed(2)}</TableCell>
                  <TableCell align="center">
                    {e.invoice_file_path ? (
                      <Tooltip title={t('medical.viewInvoice')}>
                        <IconButton size="small" onClick={() => window.open(`${API_URL}${encodeFilePath(e.invoice_file_path)}`, '_blank')}>
                          <AttachFileIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={t('common.edit')}>
                      <IconButton size="small" color="primary" onClick={() => openMeEdit(e)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={t('common.delete')}>
                      <IconButton size="small" color="error" onClick={() => setMeDeleteTarget(e)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Generic / multi-cat invoice: same dialog used by the Gatti Esterni page */}
      <MunicipalityExpenseDialog
        open={meDialogOpen}
        onClose={() => setMeDialogOpen(false)}
        municipalities={municipalities}
        expense={meEditing}
        onSaved={(msg) => {
          setMessage({ type: 'success', text: msg });
          setTimeout(() => setMessage(null), 8000);
          loadMunExpenses();
          loadExpenses();
        }}
      />

      {/* Municipality expense delete confirm */}
      <Dialog open={!!meDeleteTarget} onClose={() => setMeDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('expense.deleteMunicipalConfirm', { muni: meDeleteTarget?.municipality, amount: Number(meDeleteTarget?.amount || 0).toFixed(2) })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeDeleteTarget(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleMeDelete}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('medical.medicalEvent')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('medical.eventType')}</InputLabel>
                <Select
                  value={editFormData.event_type || ''}
                  label={t('medical.eventType')}
                  onChange={(e) => setEditFormData({ ...editFormData, event_type: e.target.value })}
                >
                  {['Vaccine', 'Sterilization', 'Treatment', 'Delivery', 'Surgery', 'Checkup', 'test_fiv_felv', 'Travel Expenses', 'Other'].map(eventType => (
                    <MenuItem key={eventType} value={eventType}>{eventType}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {editFormData.event_type === 'Sterilization' && selectedExpense?.external && (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={(
                    <Checkbox
                      checked={!editFormData.recovery_waived}
                      onChange={(e) => setEditFormData({
                        ...editFormData, recovery_waived: !e.target.checked,
                      })}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('sterilizationBatch.recoveryRecognised')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('sterilizationBatch.recoveryRecognisedHint')}
                      </Typography>
                    </Box>
                  )}
                />
              )}
            </Grid>
            <Grid item xs={6}>
              <DateField
                label={t('medical.eventDate')}
                value={editFormData.event_date || ''}
                onChange={(v) => setEditFormData({ ...editFormData, event_date: v })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" label={t('medical.costEuro')} type="text" inputMode="decimal"
                value={editFormData.cost || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setEditFormData({ ...editFormData, cost: v });
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth size="small" label={t('medical.veterinaryName')}
                value={editFormData.veterinary_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, veterinary_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label={t('common.notes')} multiline rows={2}
                value={editFormData.notes || ''}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<AttachFileIcon />} size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>
                {selectedFile ? selectedFile.name : t('medical.selectInvoiceFile')}
                <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleUpdateExpense} variant="contained">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('common.deleteConfirmation')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteExpense} color="error" variant="contained">{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ExpensesList;
